import { RouteLocationNormalizedGeneric, useRoute, useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useRootStore } from '@/micro/store/root'
import { routesWindowTitleConfig } from '@/micro/modules/window-titles'
import { resolveLoginCountrySeo } from '@/micro/modules/seo'
import { getPayrollPath, getPayrollPathFromURL, getPOSPath, getPOSPathFromURL, getQueryParameterByName } from '@/micro/utils/urls'
import {
  buildIcpRegisterNavigationUrl,
  resolveIcpLoginPathFromRegister,
  resolveIcpRegisterPathFromLogin,
} from '@/micro/utils/icpAuthNavigation'
import { isNominaIcpRegisterCountry } from '@/micro/modules/version'
import { BASE_URL } from '@/micro/utils/environment'
import type { ApplicationVersion } from '@/micro/types/version.type'

export default function () {
  const router = useRouter()
  const route = useRoute()
  const rootStore = useRootStore()
  const { product, productName, country } = storeToRefs(rootStore)

  const getUserReferrer = () => document.referrer.toLowerCase()

  const getWindowTitle = (title: string, productName: string = '') => {
    if (title.includes('{productName}') && productName) {
      return title.replace('{productName}', productName)
    }
    return title
  }

  const setWindowTitle = (route: RouteLocationNormalizedGeneric) => {
    const routeName = route?.name

    if (!routeName || typeof routeName !== 'string') return

    if (routeName === 'login-by-country') {
      const countrySeo = resolveLoginCountrySeo(route.params.country)

      if (countrySeo) {
        document.title = countrySeo.title
        return
      }
    }

    // Determinar el tipo de ruta basado en el nombre
    let routeType = 'default'
    if (routeName.includes('register')) {
      routeType = 'register'
    } else if (routeName.includes('login')) {
      routeType = 'login'
    }

    const config = routesWindowTitleConfig[routeType]
    if (!config) return

    // Usar el nombre de la ruta para buscar el mensaje específico, o el default si no existe
    const title = routeName in config.routes ? config.routes[routeName] : config.default

    document.title = getWindowTitle(title, productName.value)
  }

  const goToLogin = () => {
    const icpLoginPath = resolveIcpLoginPathFromRegister()
    if (icpLoginPath) {
      window.location.replace(`${BASE_URL}${icpLoginPath}`)
      return
    }

    const posPath = getPOSPathFromURL() || getPOSPath(product.value)
    const payrollPath = getPayrollPathFromURL() || getPayrollPath(product.value)
    let path: string = 'user/login'

    if (posPath) {
      path = `user/login/${posPath}`
    }

    if (payrollPath) {
      path = `user/login/${payrollPath}`
    }

    window.location.replace(`${BASE_URL}${path}`)
  }

  const goToRegister = ({ replace = false }: { replace?: boolean } = {}) => {
    const icpRegisterTarget = resolveIcpRegisterPathFromLogin(country.value)
    if (icpRegisterTarget) {
      if (replace) {
        window.location.replace(buildIcpRegisterNavigationUrl(icpRegisterTarget, BASE_URL))
        return
      }

      router.push({
        path: `/${icpRegisterTarget.path}`,
        query: icpRegisterTarget.query,
      })
      return
    }

    const posPath = getPOSPathFromURL() || getPOSPath(product.value)
    const payrollPath = getPayrollPathFromURL() || getPayrollPath(product.value)
    let path: string = `user/register/country/${country.value}`

    if (posPath) {
      path = `user/register/${posPath}`
    }

    if (payrollPath) {
      const routeCountry =
        typeof route.params.country === 'string' ? (route.params.country as ApplicationVersion) : null
      const payrollCountry = routeCountry || country.value

      path =
        routeCountry || isNominaIcpRegisterCountry(payrollCountry)
          ? `user/register/nomina/country/${payrollCountry}`
          : 'user/register/nomina'
    }

    if (replace) {
      window.location.replace(`${BASE_URL}${path}`)
      return
    }

    router.push({ path: `/${path}` })
  }

  const getOriginLanding = () => {
    const fromUrl = getQueryParameterByName('from_url')
    if (fromUrl) return fromUrl

    // Read cookie directly from document.cookie for this specific case
    const cookieStr = document.cookie
      .split('; ')
      .find(row => row.startsWith('al-nice-to-meet-you='))

    if (cookieStr) {
      try {
        const cookieValue = decodeURIComponent(cookieStr.split('=')[1])
        // Fix the incomplete JSON
        let jsonValue = cookieValue
        if (!jsonValue.endsWith('}')) {
          jsonValue += '"}'
        }

        const cookieData = JSON.parse(jsonValue)
        return cookieData['first-landing'] || null
      } catch (error) {
        console.error('Error parsing landing cookie:', error)
        return null
      }
    }

    return null
  }

  return {
    getUserReferrer,
    setWindowTitle,
    goToLogin,
    goToRegister,
    getOriginLanding,
  }
}
